import React, { useCallback, useMemo } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { CheckCircle, XCircle, BookOpen, PenTool } from "lucide-react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Text, Button } from "../../../../core";
import { createTruncatedCell, GenericTable } from "@core/table";
import {
  BaseAssignmentResponseDtoQuestionsItem,
  TypePerformanceType,
} from "@packages/api/madlenAPI.schemas";
import { useNavigate } from "react-router-dom";

interface QuestionTableItem {
  question: number;
  type: TypePerformanceType;
  correctRate: number;
  body: string;
  difficulty: string;
}

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
    {children}
  </div>
);

const CardContent = ({ children }: { children: React.ReactNode }) => (
  <div className="">{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <Text variant="h3" className="mb-6">
    {children}
  </Text>
);

const QuestionTable = ({
  assignmentId,
  setActiveTab,
  questions,
}: {
  assignmentId: number;
  setActiveTab: (tab: string) => void;
  questions: QuestionTableItem[];
}) => {
  const { t, i18n } = useTranslation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "body",
        header: t("analytics.assignmentAnalysis.question"),
        cell: createTruncatedCell(40, true),
      },
      {
        accessorKey: "difficulty",
        header: t("assignments.generator.questions.difficulty"),
        cell: (info: any) => (
          <span>
            {t(
              `assignments.generator.questions.difficulties.${info.getValue().toLowerCase()}`,
            )}
          </span>
        ),
      },
      {
        accessorKey: "type",
        header: t("analytics.assignmentAnalysis.type"),
        cell: (info: any) => (
          <span>
            {t(`assignments.generator.questions.types.${info.getValue()}`)}
          </span>
        ),
      },
      {
        accessorKey: "correctRate",
        header: t("analytics.assignmentAnalysis.correctRate"),
        cell: (info: any) => {
          const rate = info.getValue();
          let barColor;

          if (rate >= 90) {
            barColor = "bg-green-500 dark:bg-green-400";
          } else if (rate >= 75) {
            barColor = "bg-blue-500 dark:bg-blue-400";
          } else if (rate >= 60) {
            barColor = "bg-yellow-500 dark:bg-yellow-400";
          } else {
            barColor = "bg-red-500 dark:bg-red-400";
          }

          return (
            <div className="flex items-center gap-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${barColor} transition-all`}
                  style={{ width: `${rate}%` }}
                />
              </div>
              <span className="min-w-[3rem] text-right">
                {rate.toFixed(1)}%
              </span>
            </div>
          );
        },
      },
    ],
    [t, i18n?.language, assignmentId, setActiveTab],
  );

  const table = useReactTable({
    data: questions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: useCallback(
      (row: Row<QuestionTableItem>, columnId: string, filterValue: string) => {
        const value = row.getValue(columnId);
        if (typeof value === "string") {
          return value.toLowerCase().includes(filterValue.toLowerCase());
        }
        if (typeof value === "number") {
          return value.toString().includes(filterValue);
        }
        return false;
      },
      [],
    ),
    enableSorting: true,
  });

  return <GenericTable table={table} isEmpty={!questions.length} />;
};

const SingleAssignmentAnalyticTab = ({
  assignmentId,
  setActiveTab,
  questions,
}: {
  assignmentId: number;
  setActiveTab: (tab: string) => void;
  questions: BaseAssignmentResponseDtoQuestionsItem[] | undefined;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const processedData = useMemo(() => {
    if (!questions)
      return {
        totalQuestions: 0,
        overallCorrectRate: 0,
        typePerformance: [],
        questionTableData: [],
        hasAnswers: false,
      };

    const totalQuestions = questions.length;

    const questionsWithAnswers = questions.filter(
      (q) => (q.totalAnswerCount || 0) > 0,
    );
    const hasAnswers = questionsWithAnswers.length > 0;

    if (!hasAnswers) {
      return {
        totalQuestions,
        overallCorrectRate: 0,
        typePerformance: [],
        questionTableData: [],
        hasAnswers: false,
      };
    }

    const overallCorrectRate =
      (questionsWithAnswers.reduce((acc, q) => acc + q.avgScore, 0) /
        questionsWithAnswers.length) *
      20;

    const typeColors = {
      FB: "#3b82f6",
      OE: "#22c55e",
      TF: "#f59e0b",
      TEST: "#8b5cf6",
      MATCH: "#9333ea",
    };

    // Calculate type performance
    const typeStats = questionsWithAnswers.reduce(
      (acc, q) => {
        if (!acc[q.type]) {
          acc[q.type] = {
            count: 0,
            totalScore: 0,
            color: typeColors[q.type],
          };
        }
        acc[q.type].count++;
        acc[q.type].totalScore += q.avgScore;
        return acc;
      },
      {} as Record<
        TypePerformanceType,
        { count: number; totalScore: number; color: string }
      >,
    );

    const typePerformance = Object.entries(typeStats).map(([type, stats]) => ({
      type: type as TypePerformanceType,
      count: stats.count,
      avgCorrectRate: (stats.totalScore / stats.count) * 20,
      color: stats.color,
    }));

    // Prepare question table data
    const questionTableData = questions.map((q) => ({
      question: q.id,
      type: q.type,
      correctRate: (q.totalAnswerCount || 0) > 0 ? q.avgScore * 20 : 0,
      body: q.body,
      difficulty: q.difficulty,
    }));

    return {
      totalQuestions,
      overallCorrectRate,
      typePerformance,
      questionTableData,
      hasAnswers,
    };
  }, [questions]);

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent>
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <Text
                  variant="p"
                  className="text-sm font-medium text-gray-500 dark:text-gray-100"
                >
                  {t("analytics.assignmentAnalysis.totalQuestions")}
                </Text>
                <Text variant="h1">{processedData.totalQuestions}</Text>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <Text
                  variant="p"
                  className="text-sm font-medium text-gray-500 dark:text-gray-100"
                >
                  {t("analytics.assignmentAnalysis.averageCorrectRate")}
                </Text>
                {processedData.hasAnswers ? (
                  <Text variant="h1">
                    {processedData.overallCorrectRate.toFixed(1)}%
                  </Text>
                ) : (
                  <Text variant="h1" className="text-gray-500">
                    {t("analytics.assignmentAnalysis.noAnswers")}
                  </Text>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center">
              <XCircle className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <Text
                  variant="p"
                  className="text-sm font-medium text-gray-500 dark:text-gray-100"
                >
                  {t("analytics.assignmentAnalysis.incorrectRate")}
                </Text>
                {processedData.hasAnswers ? (
                  <Text variant="h1">
                    {(100 - processedData.overallCorrectRate).toFixed(1)}%
                  </Text>
                ) : (
                  <Text variant="h1" className="text-gray-500">
                    {t("analytics.assignmentAnalysis.noAnswers")}
                  </Text>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by Question Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardTitle>
            {t("analytics.assignmentAnalysis.questionTypeDistribution")}
          </CardTitle>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={processedData.typePerformance}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="type"
                  >
                    {processedData.typePerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      value,
                      t(
                        `assignments.generator.questions.types.${props.payload.type}`,
                      ),
                    ]}
                    labelFormatter={() => null}
                  />
                  <Legend
                    formatter={(value) =>
                      t(`assignments.generator.questions.types.${value}`)
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardTitle>
            {t("analytics.assignmentAnalysis.performanceByQuestionType")}
          </CardTitle>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processedData.typePerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    tickFormatter={(value) =>
                      t(`assignments.generator.questions.types.${value}`)
                    }
                    dataKey="type"
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip
                    formatter={(value: number) => [
                      `${value.toFixed(1)}%`,
                      t("analytics.assignmentAnalysis.correctRate"),
                    ]}
                    labelFormatter={(label: string) =>
                      t(`assignments.generator.questions.types.${label}`)
                    }
                  />
                  <Bar
                    dataKey="avgCorrectRate"
                    name={`${t("analytics.assignmentAnalysis.correctRate")} %`}
                    fill="#3b82f6"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Question Analysis */}
      <Card>
        <CardTitle>
          {t("analytics.assignmentAnalysis.detailedQuestionAnalysis")}
        </CardTitle>
        <CardContent>
          <QuestionTable
            assignmentId={assignmentId}
            setActiveTab={setActiveTab}
            questions={processedData.questionTableData}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SingleAssignmentAnalyticTab;
